import { Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Plus } from "lucide-react";
import { useState } from "react";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import { useDocumentListQuery } from "web_myplann/Api/Document/useDocumentListQuery";
import type {
	DocumentDTO,
	DocumentTagResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";

type Props = {
	item: DocumentDTO;
	activeTag?: string;
	onClose: () => void;
};

export const MoveDocumentDialog = ({ item, activeTag, onClose }: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const queryClient = useQueryClient();

	const { data: documents } = useDocumentListQuery();
	const { mutate: editDocument } = useDocumentEditMutation();

	const [tags, setTags] = useState<DocumentTagResponseDTO[]>(
		documents?.tags ?? [],
	);
	const [tag, setTag] = useState("");
	const [showInput, setShowInput] = useState(false);
	const [selectedTags, setSelectedTags] = useState<DocumentTagResponseDTO[]>(
		activeTag
			? (documents?.tags?.filter((tag) => tag.title === activeTag) ?? [])
			: [],
	);

	const handleCreateTag = () => {
		const newTag = { id: 0, title: tag };
		setTags([...tags, newTag]);
		setSelectedTags([...selectedTags, newTag]);
		setShowInput(false);
		setTag("");
	};

	const handleMoveDocument = () => {
		editDocument({
			requestContract: {
				title: null,
				tags: selectedTags.map((tag) => tag.title),
			},
			id: item.id,
			activeTag,
		});
		queryClient.invalidateQueries({
			queryKey: DocumentKeys.list(activeTag ?? undefined),
		});
		onClose();
	};

	return (
		<Stack>
			{tags.map((item) => (
				<Stack
					key={item.title}
					onClick={() =>
						setSelectedTags(
							selectedTags.includes(item)
								? selectedTags.filter((tag) => tag.title !== item.title)
								: [...selectedTags, item],
						)
					}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						px={1.6}
					>
						<Stack py={1.6}>
							<Typography>{item.title}</Typography>
						</Stack>
						{selectedTags.includes(item) && (
							<Check
								size={theme.iconSize.sm}
								color={theme.palette.primary.main}
							/>
						)}
					</Stack>
					<Divider />
				</Stack>
			))}
			<Stack
				direction="row"
				alignItems="center"
				gap={1.6}
				p={1.6}
				onClick={() => setShowInput(true)}
			>
				<Plus />
				<Typography>
					{t(Resources.Portfolio.List.MoveDocumentDialog.Actions.create)}
				</Typography>
			</Stack>
			{showInput && (
				<Stack p={1.6} gap={1}>
					<MyPlannInput
						label={t(
							Resources.Portfolio.List.MoveDocumentDialog.Actions.create,
						)}
						value={tag}
						onChange={(e) => setTag(e.target.value)}
					/>
					<Button onClick={handleCreateTag} disabled={tag.trim().length === 0}>
						{t(Resources.Common.save)}
					</Button>
				</Stack>
			)}
			<Stack px={1.6} pb={1.6}>
				{!showInput && (
					<Button onClick={handleMoveDocument}>
						{t(Resources.Common.save)}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
